/**
 * Admin V3 — Compliance — Hero Metric (G11.2)
 *
 * Prominent top-of-dashboard counter: "X documents awaiting your review"
 * with a clear CTA. Owner G11.2 acceptance criterion:
 *   "immediately see documents awaiting review"
 *
 * Empty-state: celebratory "all reviewed" message to confirm the queue is
 * empty (not a loading or error state).
 *
 * Pure presentation — count is computed by the parent from
 * `pending_verifications.length`.
 */

import React from 'react';
import { Bell, CheckCircle2 } from 'lucide-react';
import { t } from '../../i18n';

const CompliancePilotHeroMetric = ({ count = 0, locale = 'fr', onReviewClick }) => {
  if (count === 0) {
    return (
      <div
        className="rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-5 flex items-center gap-4"
        data-testid="compliance-hero-empty"
      >
        <CheckCircle2 className="h-8 w-8 text-emerald-600 shrink-0" />
        <div>
          <p className="text-emerald-900 font-medium">
            {t('compliance.hero.title', locale)}
          </p>
          <p className="text-emerald-700 text-sm">
            {t('compliance.hero.empty', locale)}
          </p>
        </div>
      </div>
    );
  }

  const label = count === 1
    ? t('compliance.hero.count.singular', locale)
    : t('compliance.hero.count.plural', locale);

  return (
    <div
      className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 px-6 py-5 flex items-center justify-between gap-4"
      data-testid="compliance-hero-pending"
    >
      <div className="flex items-center gap-4">
        <div className="rounded-full bg-amber-100 p-3">
          <Bell className="h-7 w-7 text-amber-700" />
        </div>
        <div>
          <p className="text-slate-500 text-xs uppercase tracking-wider font-medium">
            {t('compliance.hero.title', locale)}
          </p>
          <p className="text-3xl font-semibold text-slate-900 leading-tight">
            <span data-testid="compliance-hero-count">{count}</span>{' '}
            <span className="text-base font-normal text-slate-600">{label}</span>
          </p>
        </div>
      </div>
      {typeof onReviewClick === 'function' && (
        <button
          type="button"
          onClick={onReviewClick}
          className="inline-flex items-center px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium shrink-0"
          data-testid="compliance-hero-cta"
        >
          {t('compliance.hero.cta.review', locale)}
        </button>
      )}
    </div>
  );
};

export default CompliancePilotHeroMetric;
